import React, { useCallback, useMemo } from "react";
import { path } from "ramda";
import moment from "moment";
import { Field } from "formik";
import { TextField } from "formik-material-ui";
import { TimePicker, DateTimePicker } from "formik-material-ui-pickers";
import MomentUtils from "@date-io/moment";
import { makeStyles } from "@material-ui/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import Grid from "@material-ui/core/Grid";

import NestedSelect from "../../NestedSelect";
import {
  INCLUDE_EXCLUDE_OPTIONS,
  CRITERIA_OPTIONS,
  OPERATORS,
  CRITERIA_TYPE,
} from "./constants";
import { CRITERIA_CONFIGS } from "./config";
import palette from "../../styles/palette";
import { NO_OPTIONS } from "../../../constants/strings";

const getOrExecute = (valueOrFunction, ...args) =>
  typeof valueOrFunction === "function"
    ? valueOrFunction(...args)
    : valueOrFunction;

const useStyles = makeStyles(({ typography }) => ({
  button: {
    color: palette.blue,
    marginLeft: typography.pxToRem(10),
  },
  container: {
    marginTop: typography.pxToRem(2),
    marginBottom: typography.pxToRem(8),
    maxWidth: typography.pxToRem(820),
  },
  timePickerPopover: {
    "& .MuiButton-root": {
      height: "auto",
    },
  },
}));

const CriteriaItem = ({
  onAddClick,
  onRemoveClick,
  prefixName,
  route,
  value: values,
  onValueChange,
  last,
  first,
}) => {
  const classes = useStyles();

  const criteriaConfig = CRITERIA_CONFIGS[values.criteria];
  const criteriaWithOperators = path(["operators"], criteriaConfig);
  const criteriaType = path(["type"], criteriaConfig);

  const valueName = `${prefixName}.value`;

  const createOnChange = useCallback(
    (fieldName) => (value) => {
      onValueChange(fieldName, value);
    },
    [onValueChange]
  );

  const onChangeCriteria = useCallback(
    (criteria) => {
      const defaultValue =
        CRITERIA_CONFIGS[criteria].type === CRITERIA_TYPE.DATETIME
          ? moment().toISOString()
          : getOrExecute(CRITERIA_CONFIGS[criteria].default, route);

      let newValue = {
        ...values,
        criteria,
        value: defaultValue,
        operator: CRITERIA_CONFIGS[criteria].operators ? ">" : undefined,
      };

      onValueChange(prefixName, newValue);
    },
    [onValueChange, prefixName, route, values]
  );

  const options = useMemo(() => getOrExecute(criteriaConfig?.options, route), [
    criteriaConfig,
    route,
  ]);

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <Grid container spacing={2} className={classes.container}>
        <Grid item xs={2}>
          <NestedSelect
            value={values.rule}
            options={INCLUDE_EXCLUDE_OPTIONS}
            onChange={createOnChange(`${prefixName}.rule`)}
          />
        </Grid>
        <Grid item xs={3}>
          <NestedSelect
            value={values.criteria}
            options={CRITERIA_OPTIONS}
            onChange={onChangeCriteria}
          />
        </Grid>
        {criteriaWithOperators && (
          <Grid item xs={2}>
            <NestedSelect
              value={values.operator}
              options={OPERATORS}
              onChange={createOnChange(`${prefixName}.operator`)}
            />
          </Grid>
        )}

        <Grid item xs>
          {criteriaType === CRITERIA_TYPE.SELECT && (
            <NestedSelect
              value={values.value}
              options={options}
              onChange={createOnChange(valueName)}
              disabled={!options?.length}
              placeholder={!options?.length && NO_OPTIONS}
            />
          )}
          {(criteriaType === CRITERIA_TYPE.NUMBER ||
            criteriaType === CRITERIA_TYPE.STRING) && (
            <Field
              component={TextField}
              type={criteriaType}
              name={valueName}
              variant="outlined"
              fullWidth
            />
          )}
          {criteriaType === CRITERIA_TYPE.TIME && (
            <Field
              component={TimePicker}
              inputVariant="outlined"
              variant="inline"
              format="HH:mm"
              name={valueName}
              ampm={false}
              fullWidth
              PopoverProps={{
                classes: {
                  paper: classes.timePickerPopover,
                },
              }}
            />
          )}
          {criteriaType === CRITERIA_TYPE.DATETIME && (
            <Field
              component={DateTimePicker}
              inputVariant="outlined"
              name={valueName}
              variant="inline"
              format="DD:MM:YYYY, hh:mm A"
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          )}
        </Grid>

        <Grid item xs={2}>
          <IconButton
            size="small"
            onClick={onRemoveClick}
            className={classes.button}
            data-id="add"
            disabled={first && last}
          >
            <RemoveIcon />
          </IconButton>
          {last && (
            <IconButton
              size="small"
              className={classes.button}
              onClick={onAddClick}
              data-id="remove"
            >
              <AddIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </MuiPickersUtilsProvider>
  );
};

export default CriteriaItem;
